import { useEffect, useRef, useState } from 'react';

const CACHED_TERMLY_IFRAME_HOST_ID = 'filmple-termly-iframe-cache-host';
const IFRAME_REVEAL_DELAY_MS = 140;
const termlyIFrameCache = new Map<string, HTMLIFrameElement>();

function getOrCreateCacheHost(): HTMLDivElement | null {
  if (typeof document === 'undefined') return null;

  let host = document.getElementById(CACHED_TERMLY_IFRAME_HOST_ID) as HTMLDivElement | null;
  if (host) return host;

  host = document.createElement('div');
  host.id = CACHED_TERMLY_IFRAME_HOST_ID;
  host.style.position = 'fixed';
  host.style.width = '0';
  host.style.height = '0';
  host.style.opacity = '0';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'hidden';
  host.style.zIndex = '-1';
  document.body.appendChild(host);
  return host;
}

interface CachedTermlyPolicyFrameProps {
  policyUrl: string;
  title: string;
}

function getOrCreatePolicyIframe(policyUrl: string): HTMLIFrameElement | null {
  if (typeof document === 'undefined') return null;

  let iframe = termlyIFrameCache.get(policyUrl);
  if (iframe) return iframe;

  iframe = document.createElement('iframe');
  iframe.src = policyUrl;
  iframe.loading = 'lazy';
  iframe.className = 'w-full min-h-[78vh] bg-[#0B0F14] [filter:invert(1)_hue-rotate(180deg)_brightness(.95)_contrast(.92)]';
  termlyIFrameCache.set(policyUrl, iframe);
  return iframe;
}

export function prewarmTermlyPolicyFrames(policyUrls: string[]) {
  if (typeof document === 'undefined') return;

  const cacheHost = getOrCreateCacheHost();
  if (!cacheHost) return;

  for (const policyUrl of policyUrls) {
    const iframe = getOrCreatePolicyIframe(policyUrl);
    if (!iframe) continue;

    // Never move a frame that is currently mounted on-screen.
    if (!iframe.isConnected) {
      cacheHost.appendChild(iframe);
      continue;
    }

    if (iframe.parentElement === cacheHost) continue;
  }
}

export function CachedTermlyPolicyFrame({ policyUrl, title }: CachedTermlyPolicyFrameProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    const mountNode = mountRef.current;
    if (!mountNode || typeof document === 'undefined') return;

    const iframe = getOrCreatePolicyIframe(policyUrl);
    if (!iframe) return;

    let cancelled = false;
    let revealTimerId: number | null = null;
    const scheduleReveal = () => {
      if (revealTimerId !== null) {
        window.clearTimeout(revealTimerId);
      }
      revealTimerId = window.setTimeout(() => {
        if (!cancelled) {
          setShowSkeleton(false);
        }
      }, IFRAME_REVEAL_DELAY_MS);
    };
    const handleLoad = () => {
      iframe.dataset.termlyLoaded = '1';
      scheduleReveal();
    };

    setShowSkeleton(true);
    iframe.title = title;
    mountNode.appendChild(iframe);

    if (iframe.dataset.termlyLoaded === '1') {
      scheduleReveal();
    } else {
      iframe.addEventListener('load', handleLoad);
    }

    return () => {
      cancelled = true;
      iframe.removeEventListener('load', handleLoad);
      if (revealTimerId !== null) {
        window.clearTimeout(revealTimerId);
      }
      const cacheHost = getOrCreateCacheHost();
      if (cacheHost) {
        cacheHost.appendChild(iframe);
      }
    };
  }, [policyUrl, title]);

  return (
    <div className="relative w-full min-h-[78vh]">
      <div
        ref={mountRef}
        className={`w-full min-h-[78vh] transition-opacity duration-200 ${showSkeleton ? 'opacity-0' : 'opacity-100'}`}
      />
      <div
        aria-hidden={!showSkeleton}
        className={`absolute inset-0 bg-[#0B0F14] transition-opacity duration-200 ${
          showSkeleton ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <div className="h-full w-full px-6 py-8 md:px-8 md:py-10 animate-pulse">
          <div className="h-6 w-44 rounded-md bg-white/[0.07] mb-4" />
          <div className="h-4 w-11/12 rounded bg-white/[0.06] mb-2" />
          <div className="h-4 w-10/12 rounded bg-white/[0.06] mb-2" />
          <div className="h-4 w-9/12 rounded bg-white/[0.06] mb-6" />
          <div className="h-4 w-full rounded bg-white/[0.05] mb-2" />
          <div className="h-4 w-full rounded bg-white/[0.05] mb-2" />
          <div className="h-4 w-10/12 rounded bg-white/[0.05] mb-2" />
          <div className="h-4 w-11/12 rounded bg-white/[0.05] mb-2" />
          <div className="h-4 w-8/12 rounded bg-white/[0.05]" />
        </div>
      </div>
    </div>
  );
}
